import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import api from "../api";

const ConsolidatedDataPage = () => {
  const [indicadores, setIndicadores] = useState([]);
  const [selectedIndicador, setSelectedIndicador] = useState(null);
  const [anoInicio, setAnoInicio] = useState(null);
  const [mesInicio, setMesInicio] = useState(null);
  const [anoFim, setAnoFim] = useState(null);
  const [mesFim, setMesFim] = useState(null);
  const [dados, setDados] = useState([]);
  const [mensagem, setMensagem] = useState("");
  const hoje = new Date();

  // Buscar indicadores
  useEffect(() => {
    const fetchIndicadores = async () => {
      try {
        const response = await api.get("/indicadores/ativos");
        setIndicadores(response.data);
      } catch (error) {
        console.error("Erro ao buscar indicadores:", error);
      }
    };
    fetchIndicadores();
  }, []);

  // Buscar dados consolidados
  useEffect(() => {
    if (anoInicio && mesInicio && anoFim && mesFim && selectedIndicador) {
      const fetchDados = async () => {
        try {
          const response = await api.get(
            `/lancamentos/consolidados?anoInicio=${anoInicio}&mesInicio=${mesInicio}&anoFim=${anoFim}&mesFim=${mesFim}&indicadorId=${selectedIndicador}`
          );
          setDados(response.data);
          setMensagem("");
        } catch (error) {
          console.error("Erro ao buscar dados consolidados:", error);
          setMensagem("Erro ao carregar os dados consolidados.");
          setDados([]);
        }
      };
      fetchDados();
    }
  }, [anoInicio, mesInicio, anoFim, mesFim, selectedIndicador]);

  // Geração do intervalo de anos e meses permitidos
  const anosPermitidos = Array.from(
    { length: hoje.getFullYear() - 2020 + 1 },
    (_, i) => hoje.getFullYear() - i
  );

  const mesesPermitidos = Array.from({ length: 12 }, (_, i) => i + 1);

  // Ordenação dos usuários pelos últimos dois caracteres
  const sortedDados = [...dados].sort((a, b) => {
    const suffixA = a.usuario.slice(-2);
    const suffixB = b.usuario.slice(-2);
    return suffixA.localeCompare(suffixB);
  });

  // Formatar valor baseado na unidade
  const formatValue = (value, unidade) => {
    return unidade === "qtde" ? Math.round(value) : value.toFixed(2);
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h4"
        mb={3}
        sx={{ textAlign: "center", fontWeight: "bold" }}
      >
        Dados Consolidados
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="ano-inicio-label">Ano Início</InputLabel>
          <Select
            labelId="ano-inicio-label"
            value={anoInicio || ""}
            onChange={(e) => setAnoInicio(e.target.value)}
            label="Ano Início"
          >
            {anosPermitidos.map((ano) => (
              <MenuItem key={ano} value={ano}>
                {ano}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <InputLabel id="mes-inicio-label">Mês Início</InputLabel>
          <Select
            labelId="mes-inicio-label"
            value={mesInicio || ""}
            onChange={(e) => setMesInicio(e.target.value)}
            label="Mês Início"
          >
            {mesesPermitidos.map((mes) => (
              <MenuItem key={mes} value={mes}>
                {new Date(0, mes - 1).toLocaleString("pt-BR", {
                  month: "long",
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <InputLabel id="ano-fim-label">Ano Fim</InputLabel>
          <Select
            labelId="ano-fim-label"
            value={anoFim || ""}
            onChange={(e) => setAnoFim(e.target.value)}
            label="Ano Fim"
          >
            {anosPermitidos.map((ano) => (
              <MenuItem key={ano} value={ano}>
                {ano}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <InputLabel id="mes-fim-label">Mês Fim</InputLabel>
          <Select
            labelId="mes-fim-label"
            value={mesFim || ""}
            onChange={(e) => setMesFim(e.target.value)}
            label="Mês Fim"
          >
            {mesesPermitidos.map((mes) => (
              <MenuItem key={mes} value={mes}>
                {new Date(0, mes - 1).toLocaleString("pt-BR", {
                  month: "long",
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
        <InputLabel id="indicador-label">Indicador</InputLabel>
        <Select
          labelId="indicador-label"
          value={selectedIndicador || ""}
          onChange={(e) => setSelectedIndicador(e.target.value)}
          label="Indicador"
        >
          {indicadores.map((indicador) => (
            <MenuItem key={indicador.id} value={indicador.id}>
              {indicador.descricao}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {mensagem && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {mensagem}
        </Alert>
      )}

      {!mensagem && selectedIndicador && (
        <>
          <Typography variant="h6" mb={3} sx={{ textAlign: "center" }}>
            {indicadores.find((i) => i.id === selectedIndicador)?.descricao} (
            {indicadores.find((i) => i.id === selectedIndicador)?.unidade?.nome}
            )
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Instituição</TableCell>
                  {Object.keys(dados[0] || {})
                    .filter((key) => key !== "usuario")
                    .map((key) => (
                      <TableCell key={key} align="center">
                        {`${key.slice(4).padStart(2, "0")}/${key.slice(0, 4)}`}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDados.map((item) => (
                  <TableRow key={item.usuario}>
                    <TableCell>{item.usuario}</TableCell>
                    {Object.entries(item)
                      .filter(([key]) => key !== "usuario")
                      .map(([key, value]) => (
                        <TableCell key={key} align="center">
                          {formatValue(
                            value,
                            indicadores.find((i) => i.id === selectedIndicador)
                              ?.unidade?.nome
                          )}
                        </TableCell>
                      ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ConsolidatedDataPage;
